import {
  Hotel,
  Mountain,
  ArrowRight,
  Sparkles,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Demo property configurations
const DEMO_PROPERTIES = [
  {
    key: 'demo-oceanview',
    name: 'The Waverly House',
    location: 'Santa Monica, CA',
    description: 'Refined coastal boutique hotel steps from the beach',
    tagline: 'Food & Wine, Wellness, Adventure',
    icon: Hotel,
    gradient: 'from-amber-600 to-orange-500',
    image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80',
  },
  {
    key: 'demo-mountain',
    name: 'The Pine & Summit Lodge',
    location: 'Lake Tahoe, CA',
    description: 'Intimate mountain lodge with adventure concierge',
    tagline: 'Adventure, Wellness, Food & Wine',
    icon: Mountain,
    gradient: 'from-emerald-600 to-teal-500',
    image: 'https://images.unsplash.com/photo-1518602164578-cd0074062767?w=800&q=80',
  },
];

/**
 * SimpleDemoLanding - Clean landing page for widget demos
 * Links directly to working demo properties without iframes
 */
export function SimpleDemoLanding() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-18 items-center justify-between">
            <a href="https://experiencelocal.io" className="flex items-center gap-2.5 group">
              <div className="h-10 w-10 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-300 shadow-soft">
                <span className="font-serif text-xl font-semibold text-primary">E</span>
              </div>
              <span className="font-serif text-xl font-medium tracking-tight text-foreground">
                ExperienceLocal
              </span>
            </a>
            <a
              href="https://experiencelocal.io"
              className="btn-luxury text-sm py-2.5"
            >
              Get Started <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1c1917] via-[#292524] to-[#1c1917]" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1c1917]/90 via-transparent to-[#1c1917]/50" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary border border-primary/30 text-sm font-medium mb-8 shadow-gold-glow">
              <Sparkles className="h-4 w-4" />
              Interactive Demo
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-medium text-white mb-6 leading-tight tracking-tight">
              Turn Hotel Amenities Into
              <span className="block text-[#f7e7ce]">Revenue Generators</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 mb-10 leading-relaxed max-w-3xl mx-auto">
              Book experiences, reserve pool chairs, charge to room folios, and manage everything
              from an interactive map. Try our fully-functional demo below.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span>Real booking flow</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span>Live demo data</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span>No signup required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Property Cards */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <div className="bg-card rounded-2xl shadow-luxury border border-border/50 p-6 sm:p-8">
          <h2 className="font-serif text-2xl font-medium text-foreground mb-6 text-center">
            Try a Live Demo
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {DEMO_PROPERTIES.map((property) => {
              const Icon = property.icon;
              return (
                <a
                  key={property.key}
                  href={`/?property=${property.key}`}
                  className="group relative overflow-hidden rounded-2xl border-2 border-border/50 hover:border-primary hover:shadow-gold-glow transition-all duration-300"
                >
                  <div className="absolute inset-0">
                    <img
                      src={property.image}
                      alt={property.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                  </div>

                  <div className="relative p-8">
                    <div className={cn(
                      'inline-flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-br mb-4 shadow-lg',
                      property.gradient
                    )}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-serif text-2xl font-medium text-white mb-2">{property.name}</h3>
                    <p className="text-white/80 mb-1">{property.location}</p>
                    <p className="text-white/60 text-sm mb-6">{property.tagline}</p>

                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-white font-medium group-hover:bg-white/20 transition-colors">
                      Try Demo <ExternalLink className="h-4 w-4" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl font-medium text-foreground mb-4">
            See How It Works
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Click a demo above to explore the full guest booking experience. Browse experiences,
            select dates and times, and complete a test booking.
          </p>
          <p className="text-sm text-muted-foreground">
            These demos use real data and functionality - the only difference is payments are in test mode.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1c1917] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-medium mb-6">
              Ready for Your Property?
            </h2>
            <p className="text-lg text-white/70 mb-10 leading-relaxed">
              Get your booking widget set up in minutes. No credit card required.
            </p>
            <a
              href="https://experiencelocal.io"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-amber-600 text-white font-semibold rounded-xl hover:from-primary/90 hover:to-amber-600/90 transition-all shadow-lg shadow-primary/25"
            >
              Get Started Free <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="font-serif text-sm font-semibold text-primary">E</span>
              </div>
              <span className="text-muted-foreground text-sm">
                © {new Date().getFullYear()} ExperienceLocal. All rights reserved.
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="https://experiencelocal.io" className="hover:text-foreground transition-colors">
                Website
              </a>
              <a href="https://experiencelocal.io/pricing" className="hover:text-foreground transition-colors">
                Pricing
              </a>
              <a href="mailto:hello@experiencelocal.io" className="hover:text-foreground transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
